import { useEffect, useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { useApiClient, useAuth } from '../../providers/auth-provider';
import { useProxyVaultValues } from '../../hooks/use-proxy-vault';
import { useTenants } from '../../hooks/use-tenants';

import { groupLedger } from './ledger-groups';
import { OrgTreePicker } from './OrgTreePicker';
import {
  Empty,
  Pill,
  Td,
  Th,
  inputStyle,
  mono,
  primaryButton,
  secondaryButton,
  selectStyle,
  tableStyle,
  theadRowStyle,
  fmtWhen,
} from './ui';

import type {
  ProxyVaultCapture,
  ProxyVaultConnection,
  ProxyVaultKey,
  ProxyVaultLedgerEntry,
} from '../../lib/api-client';

/**
 * The values ledger — custody made visible, and WRITE-ONLY by construction.
 *
 * No cell in this table can hold a value, because no response the server sends
 * carries one. What is shown instead is proof of state: a fingerprint that
 * changes when a rotation lands, the organization the binding belongs to, and
 * the stand-in a cell is actually holding.
 *
 * The stand-in is shown deliberately. It is not a secret — that is the whole
 * premise of the system — and seeing the exact string in the cell's environment
 * turns "why was this request refused?" into a one-minute answer.
 */
export function ValuesPage({
  connectionId,
  connection,
  actingTenantId,
}: {
  connectionId: string;
  connection: ProxyVaultConnection;
  actingTenantId?: string;
}) {
  const api = useApiClient();
  const { user } = useAuth();
  // The organization whose ledger we are READING. Descendant rows come back on
  // it too, tagged, which is why a row's own org has to drive its actions.
  const tenantId = actingTenantId ?? connection.tenant_id;
  const { values, status, reload } = useProxyVaultValues(connectionId, tenantId);
  const [binding, setBinding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolveEnv, setResolveEnv] = useState('');
  const [resolution, setResolution] = useState<string | null>(null);

  // Where a NEW binding lands. Defaults to the connection's own organization,
  // and the picker is what makes bring-your-own-values reachable: a parent
  // publishes one children-visible connection and binds each child's own
  // material into it, without ever seeing the others'.
  const [targetOrg, setTargetOrg] = useState<string>(tenantId);
  // The server already narrows `listTenants` to the caller's subtree, so this
  // cannot offer an organization the server would then refuse.
  const { tenants } = useTenants();
  const orgOptions = tenants.map((t) => ({
    id: t.id,
    label: t.external_id ?? t.name ?? t.id,
  }));
  const orgLabel = (id: string | null | undefined): string =>
    orgOptions.find((o) => o.id === id)?.label ?? id ?? '—';

  /**
   * Which organization an action on THIS ROW acts as.
   *
   * A row carries its own `tenant_id`, and using the page-level one instead was
   * a live bug: rebinding a descendant's row wrote into the parent's
   * organization, and the visibility select either 404'd or silently retargeted
   * the parent's same-named binding. Rename and Delete were hidden on those
   * rows; Bind and the select were not.
   */
  const orgOf = (entry: ProxyVaultLedgerEntry): string => entry.tenant_id ?? tenantId;

  /**
   * Which row's bind form is open.
   *
   * Keyed by name AND organization, because the ledger legitimately holds
   * several rows with the SAME name — one per organization binding into a
   * shared connection, which is the entire point of the picker above. Keying by
   * name alone opened the form on every one of them at once, and since each row
   * now submits as its OWN organization, typing into the wrong one wrote a
   * value into a different org than the row you clicked.
   */
  const rowKey = (entry: ProxyVaultLedgerEntry): string =>
    `${entry.name}::${entry.tenant_id ?? 'unbound'}`;

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setError(describeApiError(err, 'That change was refused'));
    }
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto' }} data-testid="pv-values">
      <div
        style={{
          padding: '10px 20px',
          borderBottom: '1px solid var(--border)',
          fontSize: 11,
          color: 'var(--text-soft)',
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span>
          Connection is{' '}
          {connection.visibility === 'children' ? 'visible to children' : 'private to this org'} ·
          values are write-only
        </span>
        {orgOptions.length > 1 && (
          <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <span>Bind into</span>
            <OrgTreePicker
              value={targetOrg}
              onChange={setTargetOrg}
              tenants={tenants}
              size="sm"
              hint={(t) =>
                t.id === connection.tenant_id
                  ? "this connection's org"
                  : t.id === user?.tenant_id
                    ? 'you'
                    : null
              }
              ariaLabel="Bind into"
              testId="pv-values-org"
            />
          </label>
        )}
        {targetOrg !== connection.tenant_id && connection.visibility !== 'children' && (
          // Worth saying before the bind is attempted rather than after: the
          // server refuses it, and the refusal is a 404 that reads like a bug.
          <span style={{ color: 'var(--kind-warn, #d97706)' }}>
            this connection is private, so only its own organization can bind
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            placeholder="resolve env…"
            value={resolveEnv}
            data-testid="pv-resolve-input"
            onChange={(e) => setResolveEnv(e.target.value)}
            style={{ ...inputStyle, width: 190 }}
          />
          <button
            type="button"
            style={secondaryButton}
            data-testid="pv-resolve-run"
            onClick={() =>
              void api
                .explainProxyVaultResolution(connectionId, resolveEnv, tenantId)
                .then((r) => setResolution(r.detail))
                .catch((err: unknown) => setResolution(describeApiError(err, 'Could not resolve')))
            }
          >
            Explain
          </button>
        </div>
      </div>

      {resolution && (
        <div
          data-testid="pv-resolution"
          style={{ padding: '8px 20px', fontSize: 11, color: 'var(--text-soft)', background: 'var(--bg-alt)' }}
        >
          {resolution}
        </div>
      )}

      {error && (
        <div
          data-testid="pv-values-error"
          style={{
            padding: '8px 20px',
            background: 'rgba(192,57,43,0.08)',
            color: '#c0392b',
            fontSize: 11,
            whiteSpace: 'pre-wrap',
          }}
        >
          {error}
        </div>
      )}

      {status === 'loading' && values.length === 0 && <Empty>Loading…</Empty>}
      {status === 'success' && values.length === 0 && (
        <Empty>This connection declares nothing to bind.</Empty>
      )}

      {values.length > 0 && (
        <table style={tableStyle} data-testid="pv-values-table">
          <thead>
            <tr style={theadRowStyle}>
              <Th first>Name</Th>
              <Th>Kind</Th>
              <Th>State</Th>
              <Th>Fingerprint</Th>
              <Th>Organization</Th>
              <Th>Shared</Th>
              <Th>Stand-in</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {groupLedger(values, tenantId, tenants)
              .flatMap((g) => [{ entry: g.anchor, level: 0 }, ...g.nested])
              .map(({ entry, level }, i) => (
              <ValueRow
                key={`${entry.name}-${entry.tenant_id ?? 'none'}-${i}`}
                entry={entry}
                level={level}
                // Named on the button and in the form, because an UNBOUND row
                // binds wherever the picker points — not where the row sits.
                bindTarget={entry.state === 'unbound' && targetOrg !== tenantId ? orgLabel(targetOrg) : null}
                isBinding={binding === rowKey(entry)}
                onBind={() => setBinding(rowKey(entry))}
                onCancelBind={() => setBinding(null)}
                orgLabel={orgLabel(orgOf(entry))}
                onSubmitBind={(value, visibility) =>
                  act(async () => {
                    // An UNBOUND row has no organization of its own yet, so a
                    // new binding goes wherever the picker points. A bound row
                    // rebinds in place, in its own organization.
                    await api.bindProxyVaultValue(connectionId, entry.name, value, {
                      visibility,
                      tenantId: entry.state === 'unbound' ? targetOrg : orgOf(entry),
                    });
                    setBinding(null);
                  })
                }
                onRotate={(value) =>
                  act(() => api.rotateProxyVaultValue(connectionId, entry.name, value, orgOf(entry)))
                }
                onRename={(to) =>
                  act(() => api.renameProxyVaultValue(connectionId, entry.name, to, orgOf(entry)))
                }
                onVisibility={(visibility) =>
                  act(() =>
                    api.setProxyVaultValueVisibility(connectionId, entry.name, visibility, orgOf(entry)),
                  )
                }
                onDelete={() =>
                  act(() => api.deleteProxyVaultValue(connectionId, entry.name, orgOf(entry)))
                }
              />
            ))}
          </tbody>
        </table>
      )}

      <KeysSection
        connectionId={connectionId}
        tenantId={tenantId}
        orgLabel={orgLabel}
        onError={setError}
      />
      <SessionsSection connectionId={connectionId} tenantId={tenantId} orgLabel={orgLabel} />
    </div>
  );
}

/**
 * Key rows: paste in, regenerate ours, and two fingerprints.
 *
 * The paste box is the ONLY way a key gets in. No file picker, no drag-and-drop,
 * no `<input type="file">` anywhere — a PEM is text an operator already has open
 * in an editor, and text goes straight from the clipboard into the request body
 * without touching a temp file or a browser upload buffer on the way.
 */
function KeysSection({
  connectionId,
  tenantId,
  orgLabel,
  onError,
}: {
  connectionId: string;
  tenantId: string;
  orgLabel: (id: string | null | undefined) => string;
  onError: (message: string | null) => void;
}) {
  const api = useApiClient();
  const [keys, setKeys] = useState<ProxyVaultKey[]>([]);
  const [pasting, setPasting] = useState<string | null>(null);
  const [pem, setPem] = useState('');

  const load = (): void => {
    void api
      .listProxyVaultKeys(connectionId, tenantId)
      .then((r) => setKeys(r.data))
      .catch(() => setKeys([]));
  };
  useEffect(load, [connectionId, tenantId]);

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    onError(null);
    try {
      await fn();
      load();
    } catch (err) {
      onError(describeApiError(err, 'That change was refused'));
    }
  };

  if (keys.length === 0 && pasting === null) return null;

  return (
    <div style={{ padding: '16px 20px 4px' }} data-testid="pv-keys">
      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 6 }}>Keys</div>
      <table style={tableStyle} data-testid="pv-keys-table">
        <thead>
          <tr style={theadRowStyle}>
            <Th first>Name</Th>
            <Th>Algorithm</Th>
            <Th>Theirs</Th>
            <Th>Ours</Th>
            <Th>Mounted at</Th>
            <Th>Organization</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => (
            <tr key={`${key.name}-${key.tenant_id}`} data-testid="pv-key-row">
              <Td first isMono>{key.name}</Td>
              <Td>{key.algorithm ?? '—'}</Td>
              {/* Both fingerprints, and never anything else about either key.
                  "Theirs" is comparable with openssl on the operator's own
                  machine, which is how they confirm they pasted what they
                  meant to. */}
              <Td isMono>
                <span title={key.fingerprint ?? ''}>
                  {key.fingerprint ? `${key.fingerprint.slice(0, 12)}…` : '—'}
                </span>
              </Td>
              <Td isMono>
                <span title={key.proxy_key_fingerprint ?? ''}>
                  {key.proxy_key_fingerprint ? `${key.proxy_key_fingerprint.slice(0, 12)}…` : '—'}
                </span>
              </Td>
              <Td isMono>{key.mount_at ?? '—'}</Td>
              <Td>{orgLabel(key.tenant_id)}</Td>
              <Td>
                <button
                  style={secondaryButton}
                  data-testid="pv-key-repaste"
                  onClick={() => {
                    setPem('');
                    setPasting(key.name);
                  }}
                >
                  Replace
                </button>{' '}
                <button
                  style={secondaryButton}
                  data-testid="pv-key-rotate"
                  onClick={() =>
                    act(() => api.rotateProxyVaultProxyKey(connectionId, key.name, key.tenant_id))
                  }
                  // Ours is worthless at the vendor, so this needs no
                  // coordination with anyone — which is why it is a button.
                  title="Regenerate our key. Theirs is untouched and the vendor is not involved."
                >
                  Regenerate ours
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      {pasting !== null && (
        <div style={{ marginTop: 8 }} data-testid="pv-key-paste">
          <textarea
            value={pem}
            onChange={(e) => setPem(e.target.value)}
            placeholder={'-----BEGIN PRIVATE KEY-----\n…paste the whole file, including the BEGIN and END lines…'}
            rows={8}
            spellCheck={false}
            autoComplete="off"
            data-testid="pv-key-pem"
            style={{ ...inputStyle, fontFamily: mono, width: '100%', resize: 'vertical' }}
          />
          <div style={{ marginTop: 6 }}>
            <button
              style={primaryButton}
              data-testid="pv-key-save"
              onClick={() =>
                act(async () => {
                  await api.pasteProxyVaultKey(connectionId, pasting, pem.trim(), { tenantId });
                  // Cleared the moment it is submitted, and never re-fetched:
                  // there is no read path that could repopulate it.
                  setPem('');
                  setPasting(null);
                })
              }
            >
              Save key
            </button>{' '}
            <button
              style={secondaryButton}
              onClick={() => {
                setPem('');
                setPasting(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Live vendor sessions — read-only, and deliberately so.
 *
 * A stand-in and a deadline are the whole row. The deadline is the VENDOR's,
 * read off its own token, which is why it is labelled that way rather than
 * presented as a policy of ours.
 *
 * From a child's seat on an inherited connection this shows that child's own
 * sessions and nothing else — not a sibling's, not the parent's.
 */
function SessionsSection({
  connectionId,
  tenantId,
  orgLabel,
}: {
  connectionId: string;
  tenantId: string;
  orgLabel: (id: string | null | undefined) => string;
}) {
  const api = useApiClient();
  const [rows, setRows] = useState<ProxyVaultCapture[]>([]);

  const load = (): void => {
    void api
      .listProxyVaultCaptures(connectionId, tenantId)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  };
  useEffect(load, [connectionId, tenantId]);

  if (rows.length === 0) return null;

  return (
    <div style={{ padding: '16px 20px 20px' }} data-testid="pv-sessions">
      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 6 }}>
        Captured sessions — read-only
      </div>
      <table style={tableStyle} data-testid="pv-sessions-table">
        <thead>
          <tr style={theadRowStyle}>
            <Th first>Capture</Th>
            <Th>Organization</Th>
            <Th>Stand-in</Th>
            <Th>Expires</Th>
            <Th>Last used</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} data-testid="pv-session-row">
              <Td first isMono>{row.name}</Td>
              <Td>{orgLabel(row.using_tenant_id)}</Td>
              {/* The handle a cell is holding. Not a secret — that is the
                  premise — and seeing the exact string turns "why was this
                  refused?" into a one-minute answer. */}
              <Td isMono>{row.stand_in.slice(0, 24)}…</Td>
              <Td>
                <span
                  title={
                    row.expiry_source === 'platform_default'
                      ? 'The vendor gave us nothing to go on, so a platform ceiling applies'
                      : "Read off the vendor's own token — never ours to extend"
                  }
                >
                  {fmtWhen(row.expires_at)}
                </span>
                {row.expiry_source === 'platform_default' && <Pill>ceiling</Pill>}
              </Td>
              <Td>{fmtWhen(row.last_used_at)}</Td>
              <Td>
                <button
                  style={secondaryButton}
                  data-testid="pv-session-revoke"
                  onClick={() =>
                    void api
                      .revokeProxyVaultCapture(connectionId, row.id, tenantId)
                      .then(load)
                      .catch(() => load())
                  }
                  title="Delete this session. The next call from that organization simply logs in again."
                >
                  Revoke
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const NESTED_INDENT = 14;

function ValueRow({
  entry,
  level,
  bindTarget,
  isBinding,
  orgLabel,
  onBind,
  onCancelBind,
  onSubmitBind,
  onRotate,
  onRename,
  onVisibility,
  onDelete,
}: {
  entry: ProxyVaultLedgerEntry;
  /** 0 = the page organization's own row; ≥1 = another organization's row nested under it. */
  level: number;
  /** Where an unbound row's Bind will land when that is not this page's organization. */
  bindTarget: string | null;
  isBinding: boolean;
  orgLabel: string;
  onBind: () => void;
  onCancelBind: () => void;
  onSubmitBind: (value: string, visibility: 'private' | 'children') => void;
  onRotate: (value: string) => void;
  onRename: (to: string) => void;
  onVisibility: (visibility: 'private' | 'children') => void;
  onDelete: () => void;
}) {
  const [value, setValue] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'children'>('private');
  const [renameTo, setRenameTo] = useState<string | null>(null);

  const isCapture = entry.kind === 'capture';
  const isUnbound = entry.state === 'unbound';

  return (
    <>
      <tr
        style={{ borderBottom: '1px solid var(--border-soft)' }}
        data-testid="pv-value-row"
        data-level={level}
      >
        <Td first>
          {/* DESIGN_DEVIATION: the prototype's tables are flat lists. The ledger legitimately
              holds one row per organization under one name (values.service.ts ledgerFor), so
              other organizations' rows nest under the page organization's own row with a tree
              connector instead of reading as duplicates. */}
          <div
            style={{ display: 'flex', alignItems: 'baseline', gap: 6, paddingLeft: level * NESTED_INDENT }}
            {...(level > 0 ? { 'data-testid': 'pv-value-nested' } : {})}
          >
            {level > 0 && (
              <span aria-hidden style={{ color: 'var(--text-mute)', fontFamily: mono }}>
                └
              </span>
            )}
            <div>
              <div
                style={{
                  fontWeight: level > 0 ? 400 : 500,
                  ...(level > 0 ? { color: 'var(--text-mute)' } : {}),
                }}
              >
                {entry.name}
              </div>
              {entry.overridden_by && (
                <div style={{ fontSize: 10, color: 'var(--text-mute)', marginTop: 2 }}>
                  overridden by {entry.overridden_by}
                </div>
              )}
            </div>
          </div>
        </Td>
        <Td>{entry.kind}</Td>
        <Td>
          {entry.state}
          {entry.inherited && (
            <div style={{ marginTop: 3 }}>
              <Pill>inherited</Pill>
            </div>
          )}
          {entry.descendant && (
            <div style={{ marginTop: 3 }}>
              <Pill>descendant</Pill>
            </div>
          )}
        </Td>
        <Td isMono>
          {entry.fingerprint ?? '—'}
          {entry.version !== null && (
            <div style={{ fontSize: 10, color: 'var(--text-mute)' }}>
              v{entry.version} · {fmtWhen(entry.rotated_at ?? entry.bound_at)}
            </div>
          )}
        </Td>
        <Td isMono>
          {entry.tenant_external_id ?? entry.tenant_name ?? entry.tenant_id ?? '—'}
          {(entry.inherited || entry.descendant) && (
            <div style={{ fontSize: 10, color: 'var(--text-mute)' }}>
              {/* Named explicitly, because every action on this row acts as
                  THIS organization — not the one whose page you are on. */}
              acts as {orgLabel}
            </div>
          )}
        </Td>
        <Td>
          {isCapture ? (
            <span style={{ color: 'var(--text-mute)' }}>
              {entry.visibility === 'children' ? 'shared session' : 'per-session'}
            </span>
          ) : entry.visibility ? (
            <select
              value={entry.visibility}
              data-testid="pv-value-visibility"
              onChange={(e) => onVisibility(e.target.value as 'private' | 'children')}
              style={{ ...selectStyle, padding: '3px 6px', fontSize: 11 }}
            >
              <option value="private">private</option>
              <option value="children">children</option>
            </select>
          ) : (
            '—'
          )}
        </Td>
        <Td isMono>
          {entry.stand_in ? (
            <span style={{ color: 'var(--text-soft)' }}>{entry.stand_in}</span>
          ) : isCapture ? (
            <span style={{ color: 'var(--text-mute)' }}>minted at runtime</span>
          ) : (
            '—'
          )}
        </Td>
        <Td>
          {isCapture ? (
            <span style={{ fontSize: 10, color: 'var(--text-mute)' }}>nothing to upload</span>
          ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={onBind}
                style={{ ...secondaryButton, padding: '3px 8px', fontSize: 10 }}
                data-testid="pv-bind-open"
              >
                {isUnbound ? (bindTarget ? `Bind → ${bindTarget}` : 'Bind') : 'Rebind'}
              </button>
              {!isUnbound && !entry.inherited && !entry.descendant && (
                <>
                  <button
                    type="button"
                    onClick={() => setRenameTo(entry.name)}
                    style={{ ...secondaryButton, padding: '3px 8px', fontSize: 10 }}
                    data-testid="pv-value-rename"
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={onDelete}
                    style={{ ...secondaryButton, padding: '3px 8px', fontSize: 10, color: '#c0392b' }}
                    data-testid="pv-value-delete"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
        </Td>
      </tr>

      {renameTo !== null && (
        <tr>
          <td colSpan={8} style={{ padding: '10px 20px', background: 'var(--bg-alt)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                Move the binding for <strong>{entry.name}</strong> to
              </span>
              <input
                autoFocus
                value={renameTo}
                data-testid="pv-rename-input"
                onChange={(e) => setRenameTo(e.target.value)}
                style={{ ...inputStyle, width: 220 }}
              />
              <button
                type="button"
                style={primaryButton}
                data-testid="pv-rename-submit"
                disabled={!renameTo || renameTo === entry.name}
                onClick={() => {
                  onRename(renameTo);
                  setRenameTo(null);
                }}
              >
                Rename
              </button>
              <button type="button" style={secondaryButton} onClick={() => setRenameTo(null)}>
                Cancel
              </button>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-mute)', marginTop: 6 }}>
              {/* The thing an operator most needs to know, and the thing a
                  `window.prompt` had no room to say. */}
              This moves the stored material to a new name in the ledger — the value itself is
              untouched and the stand-in is kept, so nothing is re-stamped. It does NOT rename the
              connection, and the spec must be updated to declare{' '}
              <code style={{ fontFamily: mono }}>{renameTo || 'the new name'}</code> or this binding
              will have nothing to serve.
            </div>
          </td>
        </tr>
      )}

      {isBinding && (
        <tr>
          <td colSpan={8} style={{ padding: '10px 20px', background: 'var(--bg-alt)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <textarea
                placeholder={`Value for ${entry.name} — pasted or a PEM, exactly as the vendor issued it`}
                value={value}
                data-testid="pv-bind-value"
                onChange={(e) => setValue(e.target.value)}
                spellCheck={false}
                style={{
                  ...inputStyle,
                  flex: 1,
                  minWidth: 320,
                  minHeight: 60,
                  fontFamily: mono,
                  resize: 'vertical',
                }}
              />
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as 'private' | 'children')}
                style={selectStyle}
                data-testid="pv-bind-visibility"
              >
                <option value="private">private to this org</option>
                <option value="children">children may resolve with it</option>
              </select>
              <button
                type="button"
                onClick={() => {
                  onSubmitBind(value, visibility);
                  setValue('');
                }}
                style={primaryButton}
                data-testid="pv-bind-submit"
              >
                Bind
              </button>
              {!isUnbound && (
                <button
                  type="button"
                  onClick={() => {
                    onRotate(value);
                    setValue('');
                  }}
                  style={secondaryButton}
                >
                  Rotate
                </button>
              )}
              <button type="button" onClick={onCancelBind} style={secondaryButton}>
                Cancel
              </button>
            </div>
            {isUnbound && bindTarget && (
              <div style={{ fontSize: 10, color: 'var(--text-soft)', marginTop: 6 }} data-testid="pv-bind-target">
                Binding into <strong>{bindTarget}</strong>
              </div>
            )}
            <div style={{ fontSize: 10, color: 'var(--text-mute)', marginTop: 6 }}>
              Write-only. Once bound, no endpoint and no page will ever show this value again — only
              its fingerprint.
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
